import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { splitLocalizedName } from '@/lib/splitHostName';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { useParams } from '@tanstack/react-router';
import * as React from 'react';
import { AddHostGalleryForm } from '../AddHostGalleryForm';
import { useHostGalleryMutation } from '../hooks/useHostGalleryMutation';
import { getColumns } from './Column';

export function HostNameGalleryTable({
  data,
  isLoading,
  pagination,
  commonHostId,
}: {
  data: GalleryManagement[];
  isLoading: boolean;
  pagination: Pagination;
  commonHostId: additionalValue;
}) {
  const { openConfirmation } = useConfirmationStore();
  const { mutate } = useHostGalleryMutation();
  const { onOpen } = useDialogFormStore();
  const { t, locale } = useDynamicLocale();
  const params = useParams({
    from: '/(app)/store-information/basic-stores/host-management/$clubid/gallery/$host-gallery',
  });

  const clubId = params['clubid'] || '';
  const hostId = params['host-gallery'] || '';
  const columns = React.useMemo(
    () => getColumns(mutate, openConfirmation, onOpen, t),
    [mutate, openConfirmation, t, onOpen],
  );
  const onAddNew = () => {
    onOpen({
      modalTitle: 'storeManagement.galleryManagement.page.addGallery.addImage',
      title: 'Manage HostNameGallery',
      okText: t('storeManagement.basicStores.galleryManagement.button.submit'),
      size: 'lg',
      component: <AddHostGalleryForm clubId={clubId} hostId={hostId} />,
    });
  };
  const { data: options } = useDropdown(
    'hostName',
    commonHostId?.commonHostId,
    undefined,
    undefined,
    Boolean(commonHostId?.commonHostId),
  );
  const { japanese, english } = splitLocalizedName(options?.[0]?.label);
  const hostNameForTitle = locale === 'en' ? english : japanese;
  const { table } = useDataTable({
    data,
    columns,
    pageCount: 0,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: `${hostNameForTitle} ${t('storeManagement.galleryManagement.page.table.title')}`,
      actionKey: 'onAddNew',
      action: onAddNew,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
